import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import PublicScheduleLayout from '../PublicSchedule/components/PublicScheduleLayout';
import PublicScheduleHeader from '../PublicSchedule/components/PublicScheduleHeader';
import TrustFooter from '../PublicSchedule/components/TrustFooter';
import PatientRegistrationForm from './PatientRegistrationForm';

import {
    GetPublicPatientRegistrationAPI,
    SubmitPublicPatientRegistrationAPI,
} from '../../api/PublicPatientRegistrationAPI';

import '../PublicSchedule/PublicSchedulePage.css';
import './PublicPatientRegistrationPage.css';

/**
 * PublicPatientRegistrationPage
 *
 * Página pública (sem login) do link de cadastro: o paciente preenche os
 * próprios dados e entra direto na lista de pacientes da clínica. O link é de
 * uso único e expira (ver docs/features/patient-registration-link/README.md).
 *
 * Rota: /cadastro/:token
 */

const IconCheckCircle = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <polyline points="8 12 11 15 16 9" />
    </svg>
);

const IconAlert = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const STATE_TITLES = {
    invalid: 'Link inválido',
    expired: 'Link expirado',
    used: 'Cadastro já enviado',
    released: 'Reserva encerrada',
    blocked: 'Muitas tentativas',
    error: 'Não foi possível abrir o cadastro',
};

/** Texto final: horário agendado, horário reservado que já passou, ou só o cadastro. */
function doneText(appointmentWhen, missedSlot) {
    if (appointmentWhen) {
        return `Obrigado! Seu horário está agendado para ${appointmentWhen}. Seus dados foram enviados. Pode fechar esta página.`;
    }
    if (missedSlot) {
        return `Obrigado! Seus dados foram enviados. O horário que estava reservado (${missedSlot.when}) não está mais disponível: a clínica vai falar com você para combinar um novo. Pode fechar esta página.`;
    }
    return `Obrigado! Seus dados foram enviados. Pode fechar esta página.`;
}

function StateCard({ variant, title, text }) {
    return (
        <div className={`ppr-state ppr-state--${variant}`}>
            <div className="ppr-state__icon">{variant === 'success' ? <IconCheckCircle /> : <IconAlert />}</div>
            <h2 className="ppr-state__title">{title}</h2>
            {text && <p className="ppr-state__text">{text}</p>}
        </div>
    );
}

export default function PublicPatientRegistrationPage() {
    const { token } = useParams();

    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('');
    const [company, setCompany] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    // Cadastro de uma reserva de horário: o backend devolve o agendamento criado
    const [appointmentWhen, setAppointmentWhen] = useState('');
    // Cadastro chegou depois do horário reservado: virou só cadastro
    const [missedSlot, setMissedSlot] = useState(null);

    useEffect(() => {
        GetPublicPatientRegistrationAPI(token, (res) => {
            if (res && res.success && res.status === 'active') {
                setCompany(res.company || null);
                setStatus('active');
                return;
            }
            setMessage((res && res.error) || '');
            setStatus((res && STATE_TITLES[res.status]) ? res.status : 'error');
        });
    }, [token]);

    const handleSubmit = (data) => {
        setSubmitting(true);
        setGeneralError('');
        SubmitPublicPatientRegistrationAPI(token, data, (res) => {
            setSubmitting(false);
            if (res && res.success) {
                setAppointmentWhen(res.appointment?.when || '');
                setMissedSlot(res.missed_slot || null);
                setStatus('done');
                window.scrollTo(0, 0);
                return;
            }
            if (res && res.status === 'validation') {
                setErrors(res.errors || {});
                setGeneralError(res.error || 'Confira os campos destacados.');
                window.scrollTo(0, 0);
                return;
            }
            // Link deixou de valer (expirou, já usado, reserva encerrada) ou o IP foi bloqueado: tela de estado
            if (res && ['invalid', 'expired', 'used', 'released', 'blocked'].includes(res.status)) {
                setMessage(res.error || '');
                setStatus(res.status);
                return;
            }
            setErrors({});
            setGeneralError((res && res.error) || 'Não foi possível enviar. Tente novamente.');
            window.scrollTo(0, 0);
        });
    };

    return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={company} />

            <main className="ppr-main">
                {status === 'loading' && (
                    <div className="ppr-state">
                        <div className="ppr-spinner" aria-hidden="true" />
                        <p className="ppr-state__text">Carregando o cadastro...</p>
                    </div>
                )}

                {status === 'active' && (
                    <PatientRegistrationForm
                        clinicName={company?.name || ''}
                        submitting={submitting}
                        errors={errors}
                        generalError={generalError}
                        onSubmit={handleSubmit}
                    />
                )}

                {status === 'done' && (
                    <StateCard
                        variant="success"
                        title="Cadastro enviado"
                        text={doneText(appointmentWhen, missedSlot)}
                    />
                )}

                {STATE_TITLES[status] && (
                    <StateCard variant="error" title={STATE_TITLES[status]} text={message} />
                )}
            </main>

            <TrustFooter />
        </PublicScheduleLayout>
    );
}
